import { useState, useEffect, useMemo } from 'react';
import { evaluatePoint } from '../lib/evaluator';

/**
 * Hook that computes trend deltas for STF scan points by comparing
 * the current scan against 3 baselines (last run, ~1 week, ~1 month).
 *
 * Returns a Map of posKey → { lastRun, lastWeek, lastMonth } where each
 * period contains a per-yaw map: yawDegrees → { delta, currentScore, baselineScore, scannedAt }
 *
 * @param {object} api - HarvestTimeAPI instance
 * @param {string} zoneName - STF zone name
 * @param {string} currentTestRunId - TestRunId of the current scan
 * @param {Array} currentPoints - Current capture points (with _isSTFScan)
 * @param {object|null} activeEvaluator - Evaluator config for scoring
 * @returns {{ trendData: Map|null, trendLoading: boolean }}
 */
function useSTFTrends(api, zoneName, currentTestRunId, currentPoints, activeEvaluator) {
  const [baselines, setBaselines] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch baselines when zone/testRunId changes
  useEffect(() => {
    if (!api || !zoneName || !currentTestRunId) {
      setBaselines(null);
      return;
    }

    let cancelled = false;
    async function fetchBaselines() {
      setLoading(true);
      try {
        const result = await api.getSTFTrendBaselines(zoneName, currentTestRunId);
        if (!cancelled) setBaselines(result.baselines || {});
      } catch (err) {
        console.warn('[useSTFTrends] Failed to fetch baselines:', err.message);
        if (!cancelled) setBaselines(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchBaselines();
    return () => { cancelled = true; };
  }, [api, zoneName, currentTestRunId]);

  // Compute per-position, per-yaw trend deltas
  const trendData = useMemo(() => {
    if (!baselines || !currentPoints?.length || !activeEvaluator) return null;

    // Group current points by position key, then by yaw
    const currentByPos = new Map();
    for (const p of currentPoints) {
      if (!p._isSTFScan) continue;
      const key = `${p.position.x.toFixed(1)},${p.position.y.toFixed(1)},${p.position.z.toFixed(1)}`;
      if (!currentByPos.has(key)) currentByPos.set(key, []);
      currentByPos.get(key).push(p);
    }

    // Score each point individually (per-yaw)
    const currentScoresByPosYaw = new Map();
    for (const [key, points] of currentByPos) {
      const yawScores = new Map();
      for (const p of points) {
        const yaw = p._yawDegrees ?? 0;
        const { score } = evaluatePoint(p, activeEvaluator);
        yawScores.set(yaw, score);
      }
      currentScoresByPosYaw.set(key, yawScores);
    }

    // Compute deltas for each baseline period, per yaw
    const trends = new Map();
    const periods = ['lastRun', 'lastWeek', 'lastMonth'];

    for (const [posKey, yawScores] of currentScoresByPosYaw) {
      const entry = {};
      for (const period of periods) {
        const baseline = baselines[period];
        if (!baseline?.positions?.[posKey]) {
          entry[period] = null;
          continue;
        }

        const baseMetrics = baseline.positions[posKey];
        // baseMetrics may be a single aggregate or have per-yaw data
        // Score the baseline; if it has yaw-level data, score per yaw
        const baseYawPoints = baseMetrics._yawPoints || null;
        const perYaw = new Map();

        for (const [yaw, currentScore] of yawScores) {
          let baselineScore;
          // JSON keys are strings, yaw is a number — use String(yaw) for lookup
          if (baseYawPoints && baseYawPoints[String(yaw)] != null) {
            ({ score: baselineScore } = evaluatePoint(baseYawPoints[String(yaw)], activeEvaluator));
          } else {
            // Fall back to aggregate baseline score for all yaws
            ({ score: baselineScore } = evaluatePoint(baseMetrics, activeEvaluator));
          }
          const delta = currentScore - baselineScore;
          perYaw.set(yaw, {
            delta,
            currentScore,
            baselineScore,
            scannedAt: baseline.scannedAt,
          });
        }
        entry[period] = perYaw;
      }
      trends.set(posKey, entry);
    }

    // Debug: log trend summary with metric breakdown
    if (trends.size > 0) {
      // Sample first position to compare current vs baseline metrics
      const firstPosKey = [...currentScoresByPosYaw.keys()][0];
      const firstYaw = [...(currentScoresByPosYaw.get(firstPosKey)?.keys() || [])][0];
      const sampleCurrentPoint = currentPoints.find(p => {
        const k = `${p.position.x.toFixed(1)},${p.position.y.toFixed(1)},${p.position.z.toFixed(1)}`;
        return k === firstPosKey && (p._yawDegrees ?? 0) === firstYaw;
      });
      const sampleBaseline = baselines.lastRun?.positions?.[firstPosKey];
      const sampleBaseYaw = sampleBaseline?._yawPoints?.[String(firstYaw)];
      console.log('[useSTFTrends] Sample current point metrics:', sampleCurrentPoint);
      console.log('[useSTFTrends] Sample baseline aggregate:', sampleBaseline);
      console.log('[useSTFTrends] Sample baseline yaw-specific:', sampleBaseYaw);
      console.log('[useSTFTrends] Evaluator rules:', activeEvaluator?.rules);
      console.log('[useSTFTrends] Baselines used:', Object.fromEntries(
        Object.entries(baselines).map(([k, v]) => [k, { testRunId: v.testRunId, scannedAt: v.scannedAt }])
      ));
    }

    return trends;
  }, [baselines, currentPoints, activeEvaluator]);

  return { trendData, trendLoading: loading };
}

export default useSTFTrends;
